/**
 * Helpers de fechas en UTC para el calendario `/entrenos` (M2.19).
 *
 * Sin dependencias externas: `Date` nativo + `Intl`. Se trabaja en UTC para
 * que los rangos enviados al backend (`?from=...&to=...`) sean
 * deterministas y no dependan de la TZ del navegador del usuario.
 *
 * La semana arranca en LUNES (consistente con `WeekNavigator` y el
 * `MonthCalendar` del dashboard).
 */

export function startOfMonthUTC(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

export function endOfMonthUTC(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
}

export function addDaysUTC(d: Date, n: number): Date {
  const out = new Date(d);
  out.setUTCDate(d.getUTCDate() + n);
  return out;
}

export function addMonthsUTC(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}

/**
 * Devuelve el lunes a las 00:00 UTC de la semana que contiene `d`.
 */
export function startOfWeekISO(d: Date): Date {
  const dow = (d.getUTCDay() + 6) % 7; // 0 = lunes ... 6 = domingo
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow),
  );
}

export function startOfDayUTC(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
}

export function endOfDayUTC(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999),
  );
}

export function dayKeyUTC(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isSameDayUTC(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

export function todayUTC(): Date {
  return startOfDayUTC(new Date());
}

// ---------------------------------------------------------------------------
// Formateadores
// ---------------------------------------------------------------------------

/**
 * "Lun 12" — header de columna en vista semana.
 */
export function formatDayHeader(d: Date): string {
  try {
    const raw = new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(d);
    // El short en es-ES devuelve "lun, 12"; lo normalizamos a "Lun 12".
    return raw
      .replace(',', '')
      .split(' ')
      .map((seg, i) => (i === 0 ? capitalize(stripDot(seg)) : seg))
      .join(' ');
  } catch {
    return dayKeyUTC(d);
  }
}

/**
 * "Mayo 2026"
 */
export function formatMonthYear(d: Date): string {
  try {
    const raw = new Intl.DateTimeFormat('es-ES', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(d);
    return capitalize(raw);
  } catch {
    return `${d.getUTCMonth() + 1}/${d.getUTCFullYear()}`;
  }
}

/**
 * "Martes 12 de mayo, 2026" — header de la vista Día.
 */
export function formatDateLong(d: Date): string {
  try {
    const raw = new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(d);
    return capitalize(raw);
  } catch {
    return dayKeyUTC(d);
  }
}

/**
 * "Lun 12 May" — header compacto de la vista semana / día corto.
 */
export function formatShortDate(d: Date): string {
  try {
    const raw = new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(d);
    return raw
      .split(' ')
      .map((seg) => capitalize(stripDot(seg)))
      .join(' ');
  } catch {
    return dayKeyUTC(d);
  }
}

/**
 * "07:30" — hora de un ISO en la TZ indicada. Si la TZ falla, fallback a UTC.
 */
export function formatTime(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    try {
      return new Intl.DateTimeFormat('es-ES', {
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(iso));
    } catch {
      return iso.slice(11, 16);
    }
  }
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function stripDot(s: string): string {
  return s.endsWith('.') ? s.slice(0, -1) : s;
}

// ---------------------------------------------------------------------------
// Grids
// ---------------------------------------------------------------------------

/**
 * Construye la grid 7×N del mes que contiene `displayedMonth`. Lunes
 * primero. Devuelve también el `from`/`to` ISO completo para que el caller
 * dispare UNA sola query a `/api/workouts?from=...&to=...`.
 */
export function buildMonthGrid(displayedMonth: Date): {
  fromISO: string;
  toISO: string;
  cells: Date[];
} {
  const firstOfMonth = startOfMonthUTC(displayedMonth);
  const gridStart = startOfWeekISO(firstOfMonth);

  const daysInMonth = endOfMonthUTC(displayedMonth).getUTCDate();
  const firstWeekday = (firstOfMonth.getUTCDay() + 6) % 7;
  const totalCells = firstWeekday + daysInMonth > 35 ? 42 : 35;

  const cells: Date[] = [];
  for (let i = 0; i < totalCells; i++) {
    cells.push(addDaysUTC(gridStart, i));
  }

  const first = cells[0]!;
  const last = cells[cells.length - 1]!;
  return {
    fromISO: `${dayKeyUTC(first)}T00:00:00.000Z`,
    toISO: `${dayKeyUTC(last)}T23:59:59.999Z`,
    cells,
  };
}

/**
 * Construye los 7 días Lun..Dom de la semana que contiene `d`.
 */
export function buildWeekGrid(d: Date): {
  fromISO: string;
  toISO: string;
  cells: Date[];
} {
  const start = startOfWeekISO(d);
  const cells: Date[] = [];
  for (let i = 0; i < 7; i++) cells.push(addDaysUTC(start, i));
  const last = cells[6]!;
  return {
    fromISO: `${dayKeyUTC(start)}T00:00:00.000Z`,
    toISO: `${dayKeyUTC(last)}T23:59:59.999Z`,
    cells,
  };
}

/**
 * Devuelve el rango ISO del día.
 */
export function buildDayRange(d: Date): { fromISO: string; toISO: string } {
  const key = dayKeyUTC(d);
  return {
    fromISO: `${key}T00:00:00.000Z`,
    toISO: `${key}T23:59:59.999Z`,
  };
}
